// Series and slice colours. Every series in one chart gets its own colour; the same series name keeps the
// same colour across charts whenever its preferred palette slot is free. Pure functions, so the rules are
// testable without a browser.

const GOLDEN_ANGLE = 137.508;
// Circular mean of the standardised palette hues (--chart-2..8: 190,150,80,35,8,295,255) ≈ 27°, so overflow
// colours continue the shared palette family.
const OVERFLOW_SEED_HUE = 27;

/** FNV-1a: identity, rather than result ordering, determines the preferred colour across filtering and refresh. */
export function colorHash(key: string): number {
    let hash = 2166136261;
    for (const character of key) hash = Math.imul(hash ^ character.charCodeAt(0), 16777619);
    return hash >>> 0;
}

/** Extend a base palette with deterministic on-tone OKLCH overflow colours. */
export function expandPalette(base: string[], count: number): string[] {
    if (count <= 0) return [];
    if (count <= base.length) return base.slice(0, count);
    const out = base.slice();
    for (let i = base.length; i < count; i++) {
        const step = i - base.length;
        // Three lightness tiers keep neighbouring overflow hues apart even when the golden angle lands close.
        const tier = step % 3;
        const L = tier === 0 ? 0.71 : tier === 1 ? 0.57 : 0.64;
        const C = tier === 1 ? 0.13 : 0.12;
        const H = (OVERFLOW_SEED_HUE + (step + 1) * GOLDEN_ANGLE) % 360;
        out.push(`oklch(${L.toFixed(2)} ${C.toFixed(2)} ${H.toFixed(1)})`);
    }
    return out;
}

/** Parse #rgb / #rrggbb into channels; null for anything else. */
export function hexChannels(color: string): [number, number, number] | null {
    const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
    if (!match) return null;
    const hex = match[1].length === 3 ? match[1].split('').map(c => c + c).join('') : match[1];
    return [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
}

/**
 * Drop palette entries that are visually the same as an earlier one (a preset whose accent equals a fixed
 * slot, e.g. a teal accent beside the teal slot). Colours that cannot be parsed are compared as text.
 */
export function distinctPalette(colors: string[], minDistance = 48): string[] {
    const out: string[] = [];
    for (const color of colors) {
        const rgb = hexChannels(color);
        const clash = out.some(existing => {
            if (existing.toLowerCase() === color.toLowerCase()) return true;
            const other = hexChannels(existing);
            if (!rgb || !other) return false;
            // Redmean approximation of perceptual distance.
            const r = (rgb[0] + other[0]) / 2;
            const [dr, dg, db] = [rgb[0] - other[0], rgb[1] - other[1], rgb[2] - other[2]];
            return Math.sqrt((2 + r / 256) * dr * dr + 4 * dg * dg + (2 + (255 - r) / 256) * db * db) < minDistance;
        });
        if (!clash) out.push(color);
    }
    return out;
}

/**
 * One colour per key, all distinct within the chart. Each key prefers `base[hash % base.length]`; a slot
 * contested by several keys goes to the lowest key (so the result does not depend on series order), and the
 * others move to the next free slot. Beyond the base palette, expandPalette adds overflow colours.
 * `resolve` converts generated colours (OKLCH) into a form the chart library can draw.
 */
export function assignSeriesColors(keys: string[], base: string[], resolve: (color: string) => string = c => c): string[] {
    if (keys.length === 0) return [];
    // No palette at all: start from the first on-tone overflow colour rather than a colour chosen here.
    const seed = base.length ? base : expandPalette([], 1);
    // Two series that happen to share a name are still two series: the repeat gets its own colour.
    const counts = new Map<string, number>();
    keys = keys.map(key => { const n = counts.get(key) ?? 0; counts.set(key, n + 1); return n === 0 ? key : `${key}\u0000${n}`; });
    const unique = keys;
    const palette = expandPalette(seed, Math.max(seed.length, unique.length)).map(resolve);
    // Resolution can map two generated colours to the same sRGB value; never hand out a duplicate.
    const seen = new Set<string>();
    const slots = palette.map((color, index) => {
        let value = color;
        for (let nudge = 1; seen.has(value.toLowerCase()); nudge++) value = resolve(`oklch(0.62 0.12 ${(index * GOLDEN_ANGLE + nudge * 7) % 360})`);
        seen.add(value.toLowerCase());
        return value;
    });
    const order = unique.map(key => ({ key, preferred: colorHash(key) % seed.length }))
        .sort((a, b) => a.preferred - b.preferred || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    const taken = new Array<boolean>(slots.length).fill(false);
    const assigned = new Map<string, number>();
    for (const entry of order) {
        if (!taken[entry.preferred]) { taken[entry.preferred] = true; assigned.set(entry.key, entry.preferred); }
    }
    for (const entry of order) {
        if (assigned.has(entry.key)) continue;
        let slot = entry.preferred;
        while (taken[slot]) slot = (slot + 1) % slots.length;
        taken[slot] = true; assigned.set(entry.key, slot);
    }
    return keys.map(key => slots[assigned.get(key)!]);
}

/** Blend `color` toward `background` (both hex); amount 0 keeps the colour, 1 is the background. */
export function mixHex(color: string, background: string, amount: number): string {
    const a = hexChannels(color), b = hexChannels(background);
    if (!a || !b) return color;
    return '#' + a.map((channel, i) => Math.round(channel + (b[i] - channel) * amount).toString(16).padStart(2, '0')).join('');
}

/** WCAG relative luminance of a hex colour (0 when unparseable). */
export function luminance(color: string): number {
    const rgb = hexChannels(color);
    if (!rgb) return 0;
    const [r, g, b] = rgb.map(v => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
    const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
}
